import Links from '@/shared/constants/navegacao';
import Loading from '@/shared/components/Carregando';
import Select from 'react-select';
import { AnoEMesIso, DataAmigavel, DataIso, } from '@/shared/utils/datas';
import { ApiReturn } from '@/modules/api.service';
import { CACHE_MOVIMENTOS_FILTROS } from '@/shared/constants/strings';
import { Column, DataTable } from '@/shared/components/tables/DataTable';
import { EnumerateDefensores } from '@/modules/people/defensor.service';
import { FindMovimentoDto } from '@/api.types.extra';
import { Fragment, useEffect, useState } from 'react';
import { GetDefensorias } from '@/modules/structure/defensoria/defensoria.service';
import { GetNucleos } from '@/modules/structure/nucleo/nucleo.service';
import { GetUsuarios } from '@/modules/system/usuario/usuario.service';
import { LerMovimentacoes, textoCivelCriminal } from '../movimentacao.service';
import { LerTipoMovimentacoes } from '@/modules/business/movimentacoes_tipos/movimentacaotipo.service';
import { Link } from 'react-router-dom';
import { ReadMovimentacaoTipoDto, ReadDefensoriaDto, ReadDefensorResponseDto, ReadMovimentacaoDto, ReadNucleoDto, ReadUsuarioDto } from '@/api.types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { useDataTableState } from '@/shared/components/tables/useDataTableState';

interface IOpcao {
  value: number;
  label: string;
}

export default function MovimentacaoIndex() {
  const [loading, setLoading] = useState<boolean>(true);
  const [mostrarFiltros, setMostrarFiltros] = useState(true);

  const filtrosSalvos = JSON.parse(
    localStorage.getItem(CACHE_MOVIMENTOS_FILTROS) ||
    '{"mesSelecionado": "' + AnoEMesIso(new Date()) + '"}',
  );
  const [mesSelecionado, setMesSelecionado] = useState<string>(filtrosSalvos.mesSelecionado || AnoEMesIso(new Date()));
  const [idUsuario, setIdUsuario] = useState(0);
  const [listaUsuarios, setListaUsuarios] = useState<ReadUsuarioDto[]>([]);

  const [registros, setRegistros] = useState<[ReadMovimentacaoDto[], number]>();
  const tableState = useDataTableState('referencia');

  const [defensores, setDefensores] = useState<IOpcao[]>([]);
  const [defensorId, setDefensorId] = useState<number>(filtrosSalvos.defensorId || 0);
  const [defensorias, setDefensorias] = useState<IOpcao[]>([]);
  const [defensoriaId, setDefensoriaId] = useState<number>(filtrosSalvos.defensoriaId || 0);
  const [nucleos, setNucleos] = useState<IOpcao[]>([]);
  const [nucleoId, setNucleoId] = useState<number>(filtrosSalvos.nucleoId || 0);
  const [tipos, setTipos] = useState<ApiReturn<ReadMovimentacaoTipoDto>>({ objects: [], count: 0 });
  const [tipoId, setTipoId] = useState<number>(filtrosSalvos.tipoId || 0);

  useEffect(() => {
    const filtros = { mesSelecionado, defensorId, defensoriaId, nucleoId, tipoId, };
    localStorage.setItem(CACHE_MOVIMENTOS_FILTROS, JSON.stringify(filtros));
  }, [mesSelecionado, defensorId, defensoriaId, nucleoId, tipoId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [defensoriasResult, nucleosResult, defensoresResult, tiposResult, usuariosResult] =
          await Promise.all([
            GetDefensorias(0, 1000),
            GetNucleos(0, 1000),
            EnumerateDefensores(0, 1000),
            LerTipoMovimentacoes(0, 1000, false),
            GetUsuarios({ skip: 0, take: 1000 }),
          ]);

        const mapToSelectOptions = (items: any[], labelKey: string = 'nome') =>
          items.map((item) => ({
            value: item.id,
            label: item[labelKey],
          }));

        defensoriasResult.objects.unshift({ id: 0, nome: '-' } as ReadDefensoriaDto);
        setDefensorias(mapToSelectOptions(defensoriasResult.objects));

        nucleosResult.objects.unshift({ id: 0, nome: '-' } as ReadNucleoDto);
        setNucleos(mapToSelectOptions(nucleosResult.objects));

        defensoresResult.objects.unshift({ id: 0, pessoa: { nome: '-' } } as ReadDefensorResponseDto);
        setDefensores(
          defensoresResult.objects.map((item) => ({
            value: item.id,
            label: item.pessoa?.nome || '-',
          }))
        );

        tiposResult.objects.unshift({ id: 0, descricao: '-' } as ReadMovimentacaoTipoDto);
        setTipos(tiposResult);

        const ordenados = usuariosResult[0].sort((a, b) => a.username!.localeCompare(b.username!));
        setListaUsuarios(ordenados);
      } catch (error) {
        console.error('Erro ao carregar dados iniciais:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    handleBuscar();
  }, [mesSelecionado, defensorId, defensoriaId, nucleoId, tipoId, idUsuario]);

  const handleBuscar = () => {

    const filters: FindMovimentoDto = {
      skip: (tableState.currentPage - 1) * tableState.pageSize,
      take: tableState.pageSize,
      referencia: DataIso(mesSelecionado),
      comDefensor: true,
      comLocal: true,
      comRegistros: true,
      defensor: defensorId,
      defensoria: defensoriaId,
      nucleo: nucleoId,
      tipo: tipoId,
      editadoPor: idUsuario,
      orderField: tableState.sortConfig.key,
      orderDirection: tableState.sortConfig.direction
    }

    LerMovimentacoes(filters)
      .then((result) => {
        setRegistros(result);
      })
      .catch((res) => {
        toast.error((res as string) + '');
      });
  };

  const handleChangeMes = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMesSelecionado(e.target.value);
  };

  function DataTexto(data: string | Date) {
    const formatador = new Intl.DateTimeFormat('pt-BR', { year: 'numeric', month: 'short' });
    return formatador.format(new Date(data)).toUpperCase();
  }

  let colunas: Column<ReadMovimentacaoDto>[] = [
    { key: "editadoEm", header: "Editado Em", render: (row) => format(new Date(row.data_recebimento), "dd/MM/yyyy HH:mm", { locale: ptBR }), invisible: true },
    { key: "editadoPor.username", header: "Editado Por", invisible: true },
    {
      key: "referencia", header: "Referência", render: (row) => {
        return (
          <>
            Ref.: {DataTexto(row.referencia)} | Recebido em: {DataAmigavel(row.data_recebimento)}
          </>
        )
      }
    },
    {
      key: "data_recebimento", header: "Recebimento",
      render: (row) => new Date(row.data_recebimento).toLocaleDateString('pt-BR', { timeZone: 'UTC' }), invisible: true
    },
    { key: "defensor.nome", header: "Defensor", invisible: true },
    {
      key: "defensoria.nome", header: "Local", render: (row) => {
        return (
          <>
            {row.defensoria?.nome}{row.nucleo?.nome}{row.distrito ? ' - ' + row.distrito.nome : ''}   <span className="text-muted">({textoCivelCriminal(row.civel, row.criminal)})</span>
          </>
        )
      }
    },
    { key: "observacao", header: "Observação", invisible: true },
    {
      key: "atendimentos", header: "Atendimentos", render: (row) => <span>{row.registros?.reduce((acc, cv) => cv.quantidade + acc, 0)}</span>
    },
    {
      key: "registrosDescricao", header: "Tipo Registro", invisible: true, render: (row) => {
        return (
          <>
            {row.registros && row.registros?.map((registro) => (
              <span className='col-sm-3'>
                {registro.tipo.inicial && <span className="ms-3 text-muted">(inicial) </span>}{registro.tipo?.descricao}
                <br />
              </span>
            ))}
          </>
        )
      }
    },
    {
      key: "registrosTotal", header: "Total Registro", invisible: true, render: (row) => {
        return (
          <>
            {row.registros && row.registros?.map((registro) => (
              <span className='col-sm-3'>
                {registro.quantidade}
                <br />
              </span>
            ))}
          </>
        )
      }
    },
    {
      key: "acoes", header: "Ações", render: (row) => {
        return (
          <Link className="link-secondary" to={Links.Movimentacoes_Editar + '/' + row.id + `?ehnucleo=${row.nucleo ? 'true' : 'false'}`}>
            Editar
          </Link >
        )
      }
    },
  ];

  return (
    <Fragment>
      <div className="row">
        <div className="col-md-6">
          <h3 className="pt-4">Movimentações</h3>
          <span className="blockquote-footer pb-4 text-end">
            Registro de Movimentações dos Defensores
          </span>
        </div>

        <div className="col-md-6">
          <br />
          <ul>
            <button
              id="dropdownimpressao"
              className="float-end dropdown-toggle btn btn-primary"
              title="Cadastrar relatório de movimentação"
              data-bs-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            >
              Cadastrar Registro
            </button>
            <li className="nav-item dropdown dropdown-hover">
              <div className="dropdown-menu">
                {defensorId > 0 ? (
                  <>
                    <Link
                      type="button"
                      className="dropdown-item"
                      to={
                        Links.Movimentacoes_Criar +
                        '/' +
                        defensorId +
                        '/' +
                        mesSelecionado +
                        '?ehnucleo=false'
                      }
                      title="Cadastrar Movimentação de Defensoria"
                    >
                      Defensoria
                    </Link>
                    <Link
                      type="button"
                      className="dropdown-item"
                      to={
                        Links.Movimentacoes_Criar +
                        '/' +
                        defensorId +
                        '/' +
                        mesSelecionado +
                        '?ehnucleo=true'
                      }
                      title="Cadastrar Movimentação de Núcleo"
                    >
                      Núcleo
                    </Link>
                  </>
                ) : (
                  <span>* Escolha um defensor(a) *</span>
                )}
              </div>
            </li>
          </ul>
        </div>
        <hr className="pb-4" />

        <div className='row'>

          {mostrarFiltros && (
            <div className='col-md-3'>

              <label htmlFor="mes_referencia" className="col-form-label">Mês Referência:</label>
              <input id="mes_referencia" title="Mês Referência" type="month" className="form-control mb-3" value={mesSelecionado} onChange={handleChangeMes} />

              <label htmlFor="Defensor" className="col-form-label">Defensor:</label>
              <Select id="defensor" className='mb-3' options={defensores} value={defensores.find((option) => option.value === defensorId)} onChange={(option) => setDefensorId(option?.value || 0)} />

              <label htmlFor="uiTipoSelecionado" className="col-form-label">Tipos:</label>
              <select id="uiTipoSelecionado" title="Tipo de Movimento" className="form-select mb-3" value={tipoId} onChange={(e) => setTipoId(Number(e.target.value))} >
                {tipos.objects.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.descricao}
                  </option>
                ))}
              </select>

              <label htmlFor="defensoria" className="col-form-label">Defensoria:</label>
              <Select id="defensoria" className='mb-3' options={defensorias} value={defensorias.find((option) => option.value === defensoriaId)} onChange={(option) => setDefensoriaId(option?.value || 0)} />

              <label htmlFor="nucleo" className="col-form-label">Núcleo:</label>
              <Select id="nucleo" className='mb-3' options={nucleos} value={nucleos.find((option) => option.value === nucleoId)} onChange={(option) => setNucleoId(option?.value || 0)} />

              <label htmlFor="editadoPor" className="col-form-label">Editado Por:</label>
              <select id="editadoPor" title="Editado Po" className="form-select mb-3" value={idUsuario} onChange={(e) => { setIdUsuario(Number(e.target.value)) }}>
                <option key={0} value={0}>-</option>
                {listaUsuarios.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.username}
                  </option>
                ))}
              </select>

              <div className='d-flex justify-content-center mt-3'>
                <button className="btn btn-outline-success" onClick={handleBuscar}>
                  Pesquisar
                </button>
              </div>
            </div>
          )}

          <div className={mostrarFiltros ? 'col-md-9' : 'col-md-12'}>
            {loading && <Loading />}
            {!loading && <DataTable
              columns={colunas}
              data={registros?.[0]}
              initialSortKey="editadoEm"
              onChange={handleBuscar}
              serverSide={true}
              showFilter={false}
              states={tableState}
              totalRecords={registros?.[1]}
              renderFilters={() => (
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setMostrarFiltros(!mostrarFiltros)} title='Ocultar/Mostrar filtros para melhorar a visualização da tabela.' >
                  {mostrarFiltros ? 'Ocultar Filtros' : 'Mostrar Filtros'}
                </button>
              )}
            />}
          </div>
        </div>

      </div>
    </Fragment>
  );
}
